/* eslint-disable max-lines */
import { useMemo } from 'react';
import { EElementCategory, EFieldType, EStaticType, IScheme, TElement } from '../types/calculator';
import { useComponentLayout } from './useComponentLayout';
import {
  EBenefitProgram,
  EEmploymentType,
  EIncomeConfirmation,
  EObjectType,
  ERealEstateType,
} from '../types/calculator/enums';
import {
  calculatePercentage,
  calculateValueFromPercentage,
  isFlatOrUnselected,
  isNewBuilding,
  isSecondary,
  isHouseBuilding,
  isApartments,
} from '../utils';
import { useSelector } from 'react-redux';
import { selectDisplayAllFilters } from '../selectors';
import { useApplicationContext } from '../../common/utils/applicationContext';
import { ERuntimeSettings } from '../../common/types/runtimeSettings';
import { NBSP } from 'shared/common/constants';

/**
 * Хук для получения схемы формы
 */
export const useGetFormScheme = (): IScheme => {
  const { config } = useApplicationContext();
  const layout = useComponentLayout();
  const isAllFiltersVisible = useSelector(selectDisplayAllFilters);
  const lifeInsuranceFieldEnabled = config.get<boolean>(ERuntimeSettings.lifeInsuranceFieldEnabled) ?? false;

  const downPaymentOptions = config.getStrict<number[]>(ERuntimeSettings.downPaymentOptions);
  const minDownPayment = config.getStrict<number>(ERuntimeSettings.minDownPayment);

  return useMemo(
    () =>
      ({
        elements: [
          {
            category: EElementCategory.Group,
            elements: [
              {
                category: EElementCategory.Field,
                label: 'Регион покупки',
                type: EFieldType.Suggest,
                name: 'region',
                placeholder: 'Москва',
                // обогащается после получения пропсов
                source: [{ label: 'Москва', value: '1' }],
              },
              {
                category: EElementCategory.Field,
                label: 'Стоимость недвижимости',
                placeholder: '12 000 000',
                type: EFieldType.InputNumber,
                decimalDigits: 0,
                name: 'objectCost',
                rightAdornment: '₽',
                validation: {
                  validate: (value, state) => {
                    if (Number(value) <= state.downPayment) {
                      return 'Стоимость должна быть больше первого взноса';
                    }

                    return undefined;
                  },
                  min: {
                    message: 'Минимальная сумма — 1 ₽',
                    value: 2,
                  },
                  max: {
                    message: 'Максимальная сумма — 999 999 999 ₽',
                    value: 999999999,
                  },
                  required: 'Поле нужно заполнить',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Первый взнос',
                placeholder: '4 000 000',
                type: EFieldType.InputWithOptions,
                name: 'downPayment',
                decimalDigits: 0,
                rightAdornment: '₽',
                source: state => {
                  const options = downPaymentOptions.map(option => ({
                    label: `${option} %`,
                    value: `${option}`,
                    checked: calculateValueFromPercentage(state.objectCost, option) === state.downPayment,
                  }));

                  return layout === 'small' ? options.slice(0, 4) : options;
                },
                calculateForField: (value, state) => {
                  if (state && state.objectCost) {
                    return calculateValueFromPercentage(state.objectCost, value);
                  }

                  return 0;
                },
                calculateForChips: (value, state) => {
                  if (state && state.objectCost) {
                    return calculatePercentage(state.objectCost, value);
                  }

                  return 0;
                },
                validation: {
                  validate: (value, state) => {
                    if (state.objectCost === undefined) {
                      return 'Сначала необходимо указать стоимость недвижимости';
                    }
                    if (Number(value) >= state.objectCost) {
                      return 'Взнос должен быть меньше стоимости недвижимости';
                    }
                    if (Number(value) < (state.objectCost * minDownPayment) / 100) {
                      return `Минимальный взнос - ${minDownPayment}% от стоимости недвижимости`;
                    }

                    return undefined;
                  },
                  min: {
                    message: 'Минимальная сумма — 1 ₽',
                    value: 2,
                  },
                  required: 'Поле нужно заполнить',
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Тип недвижимости',
                type: EFieldType.Chips,
                name: 'objectType',
                source: [
                  {
                    label: 'Квартира',
                    value: EObjectType.Flat,
                  },
                  {
                    label: 'Апартаменты',
                    value: EObjectType.Apartments,
                  },
                  {
                    label: 'Дом',
                    value: EObjectType.House,
                  },
                ],
              },
              {
                category: EElementCategory.Field,
                label: 'Тип объекта',
                type: EFieldType.Chips,
                name: 'realEstateType',
                source: state => [
                  { label: 'Новостройка', value: ERealEstateType.NewBuilding },
                  { label: 'Вторичка', value: ERealEstateType.Secondary },
                  {
                    label: 'Строительство дома',
                    value: ERealEstateType.HouseBuilding,
                    disabled: isApartments(state) || isFlatOrUnselected(state),
                  },
                ],
              },
              {
                category: EElementCategory.Field,
                label: 'Программа',
                type: EFieldType.Chips,
                offset: { bottom: 12 },
                name: 'benefitProgram',
                defaultValue: EBenefitProgram.NoProgram,
                source: state => {
                  const familyProgramDisable =
                    isSecondary(state) ||
                    isHouseBuilding(state) ||
                    (isNewBuilding(state) && !isFlatOrUnselected(state)) ||
                    (isNewBuilding(state) && isApartments(state));
                  const ItProgramDisable =
                    isSecondary(state) || isHouseBuilding(state) || (isNewBuilding(state) && isApartments(state));

                  return [
                    { label: 'IT-ипотека ∙ 5%', value: EBenefitProgram.It, disabled: ItProgramDisable },
                    { label: 'Семейная ∙ 6%', value: EBenefitProgram.Family, disabled: familyProgramDisable },
                    {
                      label: 'Базовая',
                      value: EBenefitProgram.NoProgram,
                    },
                  ];
                },
              },
              {
                category: EElementCategory.Field,
                label: 'Срок кредита',
                placeholder: '30 лет',
                type: EFieldType.InputWithOptions,
                decimalDigits: 0,
                name: 'loanTerm',
                rightAdornment: 'лет',
                source: [
                  { label: '15 лет', value: '15' },
                  { label: '20 лет', value: '20' },
                  { label: '30 лет', value: '30' },
                ],
                validation: {
                  min: {
                    message: 'Срок кредита не может быть меньше 1 года',
                    value: 1,
                  },
                  max: {
                    message: 'Срок кредита не может превышать 35 лет',
                    value: 35,
                  },
                  required: 'Выберите значение',
                },
              },
              ...(lifeInsuranceFieldEnabled
                ? [
                    {
                      category: EElementCategory.Field,
                      label: 'Страхование жизни',
                      placeholder: '30 лет',
                      type: EFieldType.Switch,
                      name: 'lifeInsurance',
                      tooltip: `Страхование жизни — не обязательно, но${NBSP}банк повысит ставку при отказе от${NBSP}страховки.`,
                    } as TElement,
                  ]
                : []),
              {
                category: EElementCategory.Field,
                label: 'Тип занятости',
                hidden: !isAllFiltersVisible,
                type: EFieldType.Chips,
                name: 'typeOfEmployment',
                source: [
                  { label: 'Наёмный работник', value: EEmploymentType.Employee },
                  { label: 'Самозанятый', value: EEmploymentType.SelfEmployed },
                  { label: 'ИП', value: EEmploymentType.Entrepreneur },
                  { label: 'Собственник бизнеса', value: EEmploymentType.BusinessOwner },
                ],
              },
              {
                category: EElementCategory.Field,
                type: EFieldType.Radio,
                name: 'incomeConfirm',
                hidden: !isAllFiltersVisible,
                label: 'Подтверждение дохода',
                source: [
                  {
                    label: 'С подтверждением дохода',
                    value: EIncomeConfirmation.Confirmation,
                    description: 'По документам на выбор: 2-НДФЛ, 3-НДФЛ, справке по форме банка или выписке из СФР',
                  },
                  {
                    label: 'Без подтверждения дохода',
                    value: EIncomeConfirmation.NoConfirmation,
                    description: 'По двум документам: паспорт и СНИЛС',
                  },
                ],
              },
              {
                category: EElementCategory.Field,
                label: 'Зарплатная карта',
                type: EFieldType.Select,
                hidden: !isAllFiltersVisible,
                name: 'payrollBank',
                // обогащается после получения пропсов
                source: [],
                placeholder: 'Выберите банк',
                disabled: state => state.typeOfEmployment === EEmploymentType.SelfEmployed,
              },
              {
                category: EElementCategory.Static,
                label: 'allFiltersBtn',
                type: EStaticType.MoreFiltersButton,
              },
            ],
          },
        ],
      }) satisfies IScheme,
    [lifeInsuranceFieldEnabled, isAllFiltersVisible, downPaymentOptions, layout],
  );
};
